import 'promise-polyfill/src/polyfill';
import 'regenerator-runtime/runtime';
import 'custom-event-polyfill';
import 'eligrey-classlist-js-polyfill';
import '@emeritus-tech/leaky-attributes/dist/app';
import analytics from '../../modules/analytics';
import middlewares from '../../modules/analytics/middlewares/landingPageTemplateApplication';
import bootstrapper from '../../modules/bootstrapper';
import Scroller from '../../modules/halfsiesForm/scroller';
import modals from '../../modules/modals';
import getPageChunk from '../../modules/lazyChunks';
import importRejectionHandler from '../../modules/utils/importRejectionHandler';
import transformDateToUserTimezone from '../../modules/marketing/transformDateToUserTimezone';
import loadTrackpoint from '../../modules/trackpoint/load';
import formattingNumberLocalized from '../../modules/marketing/formattingNumberLocalized';
import userBucketedInExperiment from '../../modules/utils/experiments';

const cookiebotScriptId = 'cookiebot-script';
const userCentricScriptId = 'usercentrics-cmp';

const loadCookiebot = (cookiebotId) => {
  if (document.getElementById(cookiebotScriptId)
      || document.getElementById(userCentricScriptId)
      || typeof window.Cookiebot === 'object'
      || bootstrapper.cookiesAgreementDisabled) {
    return;
  }

  let cookieBotSrc;
  let scriptId;

  if (bootstrapper.userCentricCookies) {
    cookieBotSrc = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
    scriptId = userCentricScriptId;
  } else {
    cookieBotSrc = 'https://consent.cookiebot.com/uc.js';
    scriptId = cookiebotScriptId;
  }

  const cookiebotScript = document.createElement('script');
  cookiebotScript.src = cookieBotSrc;
  cookiebotScript.type = 'text/javascript';
  cookiebotScript.id = scriptId;
  cookiebotScript.dataset.cbid = cookiebotId;

  if (cookiebotId && !bootstrapper.userCentricRulesetId) {
    cookiebotScript.dataset.settingsId = cookiebotId;
  }

  if (bootstrapper.userCentricRulesetId) {
    cookiebotScript.dataset.rulesetId = bootstrapper.userCentricRulesetId;
  }

  document.head.appendChild(cookiebotScript);
};

const initPage = () => {
  window.addEventListener('unhandledrejection', (e) => {
    importRejectionHandler(e);
  });

  analytics.init(middlewares);
  analytics.page({}, {
    trackAllPages: true,
    integrations: { 'Facebook Pixel': true },
  });
  import(/* webpackChunkName: "trackClientEvents" */ '../../modules/trackClientEvents').then(({ default: trackClientEvents }) => {
    trackClientEvents();
  });
  loadTrackpoint();

  const loadHandler = () => {
    const {
      customFormId,
      coursePreviewLandingPageEnabled,
      customFormRedirectPath,
      programFacebookPixelId,
      programName,
      programPrice,
      locale,
    } = bootstrapper;

    // Removing "Enroll now" buttons
    getPageChunk('marketing/removeEnrollNowButtons');

    // Show dates in users local time
    transformDateToUserTimezone();

    // Formatting numbers localized for the country
    formattingNumberLocalized();

    if (!bootstrapper.edgeCacheLandingPages) {
      getPageChunk('marketing/autoPrefilledCountrySelect');
    }

    if (bootstrapper.consentRadiobutton) {
      getPageChunk('marketing/consentRadiobutton');
    }

    // Add country code to phone number field
    if (
      !bootstrapper.disabledFillPhoneCountryCode || userBucketedInExperiment('e20230913_fill_phone_country_code:variant')
    ) {
      getPageChunk('marketing/addCountryCodeToPhoneNumber');
    }

    // Open Typeform popup
    const typeFormLink = document.getElementById('typeform-share');

    if (typeFormLink) {
      getPageChunk('marketing/openTypeForm');
    }

    // STICKY MICROSITE NAV
    //---------------------
    const infoBar = document.querySelector('.information-bar-component') || document.querySelector('.information');
    let selector;
    if (!infoBar && window.screen.width <= 768 && window.screen.height <= 1024) {
      selector = '.microsite-nav.mobile';
    } else {
      selector = '.microsite-nav';
    }
    let micrositeNavBar = document.querySelector(selector);
    let micrositeStickyScrollThreshold = 0;
    if (micrositeNavBar) {
      micrositeStickyScrollThreshold = micrositeNavBar ? micrositeNavBar.offsetHeight : 164;
      getPageChunk('marketing/halfsiesForm/stickyMicrositeNav', { selector, micrositeStickyScrollThreshold });
    }

    // ------------------
    //  STICKY NAV
    //--------------------
    const desktopInfoBar = document.querySelector('.information-bar-component') || document.querySelector('.information');
    // Workaround to adjust sticky top padding for mobile
    if (window.screen.width <= 768 && window.screen.height <= 1024) {
      micrositeStickyScrollThreshold = 0;
      micrositeNavBar = false;
    }
    let scrollThreshold = 0;

    if (desktopInfoBar) {
      const desktopStickyScrollThreshold = desktopInfoBar
        ? desktopInfoBar.offsetTop - micrositeStickyScrollThreshold
        : 650;
      scrollThreshold = coursePreviewLandingPageEnabled
        ? 85
        : desktopStickyScrollThreshold;
      getPageChunk('marketing/halfsiesForm/stickyNav', { micrositeNavBar, micrositeStickyScrollThreshold, scrollThreshold });
    }

    // -----------------
    // ANCHOR OFFSET //
    // -----------------

    if (desktopInfoBar || micrositeNavBar) {
      getPageChunk('marketing/halfsiesForm/anchorOffset', scrollThreshold, desktopInfoBar);
    }

    /* Workaround to adjust scroll from info bar to for b2b offering page section */
    getPageChunk('marketing/b2b_enterprise_offering/info_bar_scroll');
    getPageChunk('marketing/b2b_enterprise_offering/inquiring_radio_button');

    // ------------------
    //  VIDEO ON HERO
    //--------------------
    const heroVideoModal = document.getElementById('hero-video-modal');

    if (heroVideoModal) {
      getPageChunk('marketing/video_on_hero/video_on_hero', 'loadYouTubeIframeAPI');
    }

    // ------------------
    //  MODALS
    //--------------------
    modals();

    // ------------------
    //  REFERRALS
    //--------------------
    if (bootstrapper.incentivizedReferralsEnabled) {
      getPageChunk('referrals/referralsCustomFormText');
    }

    if (document.querySelector('.js-apply-now-button')) {
      getPageChunk('referrals/advocateParams');
    }

    // ------------------
    //  ROUNDS VERSION 2
    //--------------------
    if (document.querySelector('#countdown-timer')) {
      getPageChunk('marketing/rounds_version_2', '#countdown-timer');
    }

    // ------------------
    //  SMOOTH SCROLLING
    //--------------------
    Scroller({
      scrollBehavior: 'smooth',
    });

    // ------------------
    // COLLAPSIBLE SECTIONS
    //--------------------
    const collapsibleSection = document.getElementsByClassName('collapsible__content');
    if (collapsibleSection.length > 0) {
      import(/* webpackChunkName: "collapsibleElement" */ '../../modules/admin/collapsibleElement').then(({ default: collapsibleElement }) => {
        collapsibleElement();
      });
    }

    // ------------------
    // PROGRESSIVE LEAD FORM
    //--------------------
    const progressiveFormSection = document.getElementById('progressive-form-next-button-1');

    if (progressiveFormSection) {
      getPageChunk('marketing/landingPageTemplateProgressiveForm');
    }

    // ------------------
    // GROUP ENROLLMENT MODAL
    //--------------------
    const groupEnrollmentModalSection = document.getElementById('group-enrollment-modal');

    if (groupEnrollmentModalSection) {
      getPageChunk('marketing/landingPageTemplateGroupEnrollmentModal');
    }

    // ------------------
    // READ MORE LINKS
    //-------------------
    const readMoreSection = document.getElementsByClassName('read-more__content');
    if (readMoreSection.length > 0) {
      import(/* webpackChunkName: "readMoreElement" */ '../../modules/admin/readMoreElement').then(({ default: readMoreElement }) => {
        readMoreElement();
      });
    }

    // ------------------
    //  HALFSIES FORM
    //--------------------
    const formId = 'new_custom_form_entry';
    const form = document.getElementById(formId);
    const params = window.location.search;

    if (form) {
      import(/* webpackChunkName: "halfsiesForm" */ '../../modules/halfsiesForm').then(({ default: HalfsiesForm }) => {
        const halfsiesForm = new HalfsiesForm({
          afterSubmit: async (response) => {
            let extraParams = response.data.extra_params;
            if (extraParams !== '') {
              extraParams = `${params === '' ? '?' : '&'}${extraParams}`;
            }
            /*
              This hook is intended to be used to reload or redirect the page
              it is also where you will send events to facebook, or push things to the
              data layer in GTM, write to localstorage, etc...
            */
            if (programFacebookPixelId) {
              analytics.track('Lead', { courseName: programName, coursePrice: programPrice }, {
                fbPixel: `${programFacebookPixelId}`,
                integrations: {
                  'Facebook Pixel': true,
                },
              });
            } else {
              window.dataLayer = window.dataLayer || [];

              const leadFormSubmitData = {
                event: 'lead-form-submit-lead',
                courseName: programName,
                coursePrice: programPrice,
              };

              if (form.elements && form.elements.custom_form_entry_email) {
                leadFormSubmitData.userEmail = form.elements.custom_form_entry_email.value;
              }

              window.dataLayer.push(leadFormSubmitData);
            }

            if (window.LUX) {
              window.LUX.addData('lead-form-submit', 1);
            }

            getPageChunk('marketing/handleLeadCustomForm',
              params + extraParams,
              coursePreviewLandingPageEnabled,
            );
          },
          coursePreviewLandingPageEnabled,
          customFormId,
          customFormRedirectPath,
          form,
          formSelector: '#new_custom_form_entry.js-custom-form',
          host: window.location.origin,
          paramString: params,
          setUTMParams: true,
          submitButtonSelector: 'input[type=submit]',
        });

        if (coursePreviewLandingPageEnabled && window.location.hash === '#referrals-email-capture-modal') {
          window.location.hash = '#preview-course-modal';
        }

        // --------------------------
        //  PREFETCH AND PREFILL FORM
        //---------------------------

        halfsiesForm.fetch()
          .then((response) => {
            const { data: { data } } = response;
            data.course_preview = coursePreviewLandingPageEnabled;
            const allowedFields = [
              'country',
              'email',
              'first_name',
              'last_name',
              'phone',
              'work_experience',
              'group_enrollment',
            ];

            const findInput = (name) => {
              if (name === 'phone') {
                return document.querySelector('#custom_form_entry_mobile_no');
              }
              return document.querySelector(`#custom_form_entry_${name}`);
            };
            getPageChunk('marketing/halfsiesForm/prefillForm', data, allowedFields, findInput);

            import(/* webpackChunkName: "toggleContactCheckbox" */ '../../modules/iframes/toggleContactCheckbox').then(({ default: toggleContactCheckbox }) => {
              toggleContactCheckbox();
            });

            if (!coursePreviewLandingPageEnabled) {
              const workExperienceSelect = document.querySelector('select[id="custom_form_entry_work_experience"]');
              const countrySelect = document.querySelector('select[id="custom_form_entry_country"]');
              const countryText = '';
              const workText = '';

              if (workExperienceSelect) {
                workExperienceSelect.options[0].textContent = workText;
                workExperienceSelect.options[0].disabled = true;
              }

              if (countrySelect) {
                countrySelect.options[0].textContent = countryText;
                countrySelect.options[0].disabled = true;
              }
            }
          });

        if (locale === 'es') {
          getPageChunk('landing_page_template_latam');
        }
      });
    }

    // ------------------
    // EXPERIMENT LANDING PAGE FORM IN MODAL
    //--------------------
    const closeBtn = document.getElementById('modal-mobile-close');
    const mobileModal = document.getElementById('modal-mobile');

    if (closeBtn) {
      closeBtn.onclick = () => {
        mobileModal.classList.remove('modal--open');
      };
    }
  };

  loadHandler();

  loadCookiebot(bootstrapper.cookiebotId);

  getPageChunk('marketing/loadGtm');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPage());
} else {
  initPage();
}
